// imported from myintelli-onnx commit 9f78984 on 2026-05-04
// do not edit manually — regenerate via `node scripts/sync-onnx-vendor.mjs`
import type { QualityConfig } from './config.js';
import type { FaceDetection, ImageFrame, QualityReport } from './types.js';

/**
 * Pure-JS quality gate. No native deps so it runs identically in browser /
 * RN / Node. Implementations match `core/python/intelliid_core/quality.py`.
 */
export class QualityGate {
  constructor(private readonly cfg: QualityConfig) {}

  evaluate(frame: ImageFrame, det: FaceDetection): QualityReport {
    const [x1, y1, x2, y2] = clipBbox(det.bbox, frame.width, frame.height);
    const cropW = x2 - x1;
    const cropH = y2 - y1;
    if (cropW <= 0 || cropH <= 0) {
      return zeroReport(['degenerate detection bbox']);
    }

    const gray = grayscaleCrop(frame, x1, y1, cropW, cropH);
    const blurVar = laplacianVariance(gray, cropW, cropH);
    const brightness = mean(gray);
    const shortSide = Math.min(cropW, cropH);
    const [yawDeg, pitchDeg] = estimatePose(det.landmarks);

    const blurOk = blurVar >= this.cfg.blurMinLaplacianVar;
    const brightnessOk =
      brightness >= this.cfg.brightnessMin && brightness <= this.cfg.brightnessMax;
    const sizeOk = shortSide >= this.cfg.faceMinShortSidePx;
    const poseOk =
      Math.abs(yawDeg) <= this.cfg.yawMaxDeg && Math.abs(pitchDeg) <= this.cfg.pitchMaxDeg;

    const reasons: string[] = [];
    if (!blurOk) reasons.push(`blurry (laplacian var=${blurVar.toFixed(0)})`);
    if (!brightnessOk) reasons.push(`brightness out of range (mean=${brightness.toFixed(0)})`);
    if (!sizeOk) reasons.push(`face too small (short side=${shortSide.toFixed(0)}px)`);
    if (!poseOk)
      reasons.push(`pose off (yaw=${yawDeg.toFixed(0)}°, pitch=${pitchDeg.toFixed(0)}°)`);

    return {
      blurVar,
      brightnessMean: brightness,
      faceShortSidePx: Math.floor(shortSide),
      yawDeg,
      pitchDeg,
      blurOk,
      brightnessOk,
      sizeOk,
      poseOk,
      ok: blurOk && brightnessOk && sizeOk && poseOk,
      reasons,
    };
  }
}

function zeroReport(reasons: string[]): QualityReport {
  return {
    blurVar: 0,
    brightnessMean: 0,
    faceShortSidePx: 0,
    yawDeg: 0,
    pitchDeg: 0,
    blurOk: false,
    brightnessOk: false,
    sizeOk: false,
    poseOk: false,
    ok: false,
    reasons,
  };
}

function clipBbox(
  bbox: readonly [number, number, number, number],
  w: number,
  h: number,
): [number, number, number, number] {
  return [
    Math.max(0, Math.floor(bbox[0])),
    Math.max(0, Math.floor(bbox[1])),
    Math.min(w, Math.ceil(bbox[2])),
    Math.min(h, Math.ceil(bbox[3])),
  ];
}

function grayscaleCrop(
  frame: ImageFrame,
  x: number,
  y: number,
  w: number,
  h: number,
): Float32Array {
  const out = new Float32Array(w * h);
  const stride = bytesPerPixel(frame.format);
  const fStride = frame.width * stride;
  for (let row = 0; row < h; row++) {
    const srcRow = (y + row) * fStride;
    const dstRow = row * w;
    for (let col = 0; col < w; col++) {
      const i = srcRow + (x + col) * stride;
      // Rec. 601 luma coefficients; matches OpenCV BGR2GRAY behavior.
      let r = 0;
      let g = 0;
      let b = 0;
      if (frame.format === 'rgba' || frame.format === 'rgb') {
        r = frame.data[i] ?? 0;
        g = frame.data[i + 1] ?? 0;
        b = frame.data[i + 2] ?? 0;
      } else {
        b = frame.data[i] ?? 0;
        g = frame.data[i + 1] ?? 0;
        r = frame.data[i + 2] ?? 0;
      }
      out[dstRow + col] = 0.114 * b + 0.587 * g + 0.299 * r;
    }
  }
  return out;
}

function bytesPerPixel(format: ImageFrame['format']): number {
  return format === 'rgba' ? 4 : 3;
}

function mean(arr: Float32Array): number {
  let s = 0;
  for (let i = 0; i < arr.length; i++) s += arr[i] ?? 0;
  return arr.length ? s / arr.length : 0;
}

/** 3x3 Laplacian kernel applied via separable conv → variance of result. */
function laplacianVariance(gray: Float32Array, w: number, h: number): number {
  const out = new Float32Array(gray.length);
  // Apply [[0,1,0],[1,-4,1],[0,1,0]] — interior pixels only; borders left at 0
  for (let y = 1; y < h - 1; y++) {
    for (let x = 1; x < w - 1; x++) {
      const i = y * w + x;
      const v =
        (gray[i - w] ?? 0) +
        (gray[i - 1] ?? 0) +
        (gray[i + 1] ?? 0) +
        (gray[i + w] ?? 0) -
        4 * (gray[i] ?? 0);
      out[i] = v;
    }
  }
  // Variance over interior pixels
  let sum = 0;
  let sumSq = 0;
  let n = 0;
  for (let y = 1; y < h - 1; y++) {
    for (let x = 1; x < w - 1; x++) {
      const v = out[y * w + x] ?? 0;
      sum += v;
      sumSq += v * v;
      n++;
    }
  }
  if (n === 0) return 0;
  const m = sum / n;
  return sumSq / n - m * m;
}

function estimatePose(
  landmarks: FaceDetection['landmarks'],
): readonly [yaw: number, pitch: number] {
  const [leftEye, rightEye, nose, leftMouth, rightMouth] = landmarks;
  const eyeMidX = (leftEye[0] + rightEye[0]) / 2;
  const eyeDist = Math.max(1e-6, Math.abs(rightEye[0] - leftEye[0]));
  const yawSignal = (nose[0] - eyeMidX) / eyeDist;
  const yawDeg = (Math.atan(yawSignal * 2) * 180) / Math.PI;

  const eyeY = (leftEye[1] + rightEye[1]) / 2;
  const mouthY = (leftMouth[1] + rightMouth[1]) / 2;
  const faceHeight = Math.max(1e-6, mouthY - eyeY);
  const pitchSignal = (nose[1] - eyeY) / faceHeight - 0.5;
  const pitchDeg = (Math.atan(pitchSignal * 2) * 180) / Math.PI;

  return [yawDeg, pitchDeg];
}
